import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import WhatsappBusinessModal from 'pages/OtherIntegrations/Integrations/Modals/WhatsappBusiness/WhatsappBusinessModal';

import { ENUMS, WHATSAPP } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import { languages } from 'utils/localization';

import ContentCreator from './ContentCreator';

const ParameterMatcher = ({ fieldName, status, menuItems }) => {
	const formMethods = useFormContext();

	const {
		control,
		setValue,
		watch,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = useMemo(() => isFormLoading || isSubmitting, [isFormLoading, isSubmitting]);

	// merge data as needed

	const fetchLabels = useCallback(async () => menuItems?.map((item) => item.label), [menuItems]);

	const handleChange = useCallback(
		(value, index) => {
			setValue(`components[${index}].newParameter`, value, {
				shouldValidate: true,
				shouldDirty: true
			});
		},
		[menuItems]
	);

	const columns = useMemo(() => {
		const columnData = [
			{
				id: 'location',
				header: 'location',
				accessorKey: 'location',
				size: 40,
				renderCell: ({ row: { index } }) => (
					<Typography color="var(--black)">{watch(`components[${index}].location`)}</Typography>
				)
			},
			{
				id: 'context',
				header: 'context',
				accessorKey: 'context',
				size: 70,
				renderCell: ({ row: { index } }) => (
					<Typography color="var(--black)">{watch(`components[${index}].context`)}</Typography>
				)
			},
			{
				id: 'parameterName',
				header: 'placeholder',
				accessorKey: 'parameterName',
				size: 10,
				renderCell: ({ row: { index } }) => (
					<Typography color="var(--black)">{watch(`components[${index}].parameterName`)}</Typography>
				)
			},
			{
				id: 'newParameter',
				header: 'newParameter',
				accessorKey: 'newParameter',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						value={watch(`components[${index}].newParameter`)}
						setValue={(value) => handleChange(value, index)}
						valueRemovable={false}
						fetchFunction={fetchLabels}
					/>
				)
			}
		];

		return columnData.filter(
			(column) => status === 'needParameterCheck' && column.accessorKey !== 'parameterContext'
		);
	}, [fetchLabels, handleChange, status]);

	const data = useWatch({
		control,
		name: fieldName
	});

	return (
		<Table
			columns={columns}
			data={data}
			loading={isLoading}
			noRecordsFound={
				<NoRecordsFound lottieName="no-items" height="10rem" title="noItems" subTitle="noItemsSubtitle" />
			}
		/>
	);
};

const WhatsappTemplateForm = NiceModal.create(({ id, mode = 'add', modalName }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const { showError, showSuccess } = useAlert();

	const [editableState, setEditableState] = useState({
		contextEditable: false,
		categoryEditable: false,
		parametersEditable: false
	});
	const [status, setStatus] = useState(null);

	const title = useMemo(() => {
		const titles = {
			add: 'addWhatsappTemplate',
			edit: 'editWhatsappTemplate',
			view: 'viewWhatsappTemplate'
		};

		return titles[mode];
	}, [mode]);

	const menuItems = useMemo(
		() => [
			{
				label: 'clientName',
				action: () => {},
				input: '{{clientName}}'
			},
			{
				label: 'link',
				action: () => {},
				input: '{{link}}'
			},
			{
				label: 'orderNumber',
				action: () => {},
				input: '{{orderNumber}}'
			},
			{
				label: 'trackingNumber',
				action: () => {},
				input: '{{trackingNumber}}'
			},
			{
				label: 'deliveryCompany',
				action: () => {},
				input: '{{deliveryCompany}}'
			},
			{
				label: 'customerName',
				action: () => {},
				input: '{{customerName}}'
			},
			{
				label: 'OTP',
				action: () => {},
				input: '{{OTP}}'
			},
			{
				label: 'branchName',
				action: () => {},
				input: '{{branchName}}'
			},
			{
				label: 'branchLocation',
				action: () => {},
				input: '{{branchLocation}}'
			}
		],
		[]
	);

	// Whatsapp supported language codes : https://support.wati.io/l/en/article/8qf6ekwd88-what-are-the-supported-languages-for-template-update
	// const languageMapping = useMemo(
	// 	() => ({
	// 		engValue: {
	// 			otoCode: 'engValue',
	// 			wpCode: 'en_US'
	// 		},
	// 		trValue: {
	// 			otoCode: 'trValue',
	// 			wpCode: 'tr'
	// 		},
	// 		arabicValue: {
	// 			otoCode: 'arabicValue',
	// 			wpCode: 'ar'
	// 		}
	// 	}),
	// 	[]
	// );

	const categories = useMemo(() => ['utility', 'marketing', 'authentication'], []);

	const errorMessages = useMemo(
		() => [
			'error.WT001',
			'error.WT002',
			'error.WT003',
			'error.WT004',
			'error.WT005',
			'error.WT006',
			'error.WT007',
			'error.WT008',
			'error.WT009',
			'error.WT010',
			'error.WT099',
			'error.WA001'
		],
		[]
	);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					context: yup.string().nullable().required(),
					name: yup
						.string()
						.matches(/^(?!.*\s)(?!.*[A-Z])[a-z0-9_]*$/g, {
							message: 'error.WT001',
							excludeEmptyString: true
						})
						.nullable()
						.required(),
					language: yup.string().nullable().required(),
					category: yup.string().nullable().required(),
					buttonLabel: yup.string().nullable(),
					headerUrl: yup.string().nullable(),
					connectionID: yup.number().nullable().required(),
					components: yup
						.array()
						.of(
							yup
								.object()
								.shape({
									location: yup.string(),
									componentOTOId: yup.number(),
									parameters: yup
										.array()
										.of(
											yup
												.object()
												.shape({
													parameter: yup.string().nullable(),
													newValue: yup.string().nullable()
												})
												.nullable()
										)
										.nullable()
								})
								.nullable()
						)
						.nullable()
				})
				.required(),
		[mode, status]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			ID: null,
			context: null,
			name: null,
			language: null,
			category: null,
			connectionID: null,
			buttonLabel: null,
			headerUrl: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		setError,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = useMemo(() => isFormLoading || isSubmitting, [isFormLoading, isSubmitting]);

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const onSubmit = useCallback(
		async (data) => {
			// merge data as subitems
			const componentList = [];

			if (data.components?.length) {
				data.components.forEach((component) => {
					const isParent = componentList.some((c) => c.componentOTOId === component.componentOTOId);

					if (isParent) {
						const index = componentList.findIndex((c) => c.componentOTOId === component.componentOTOId);
						const parameterElement = {
							parameter: component.parameterName,
							newValue: component.newParameter
						};
						componentList[index].parameters.push(parameterElement);
					} else {
						const parameters = [];
						parameters.push({
							parameter: component.parameterName,
							newValue: component.newParameter
						});
						componentList.push({
							location: component.location,
							componentOTOId: component.componentOTOId,
							parameters
						});
					}
				});
			}

			const url = mode === 'add' ? WHATSAPP.ADD_WHATSAPP_TEMPLATE : WHATSAPP.UPDATE_WHATSAPP_TEMPLATE;

			const editData = {
				...(mode === 'edit' ? { ID: data.ID } : {}),
				headerUrl: data.headerUrl,
				buttonLabel: data.buttonLabel,
				...(status === 'needParameterCheck' ? { components: componentList } : {}),
				...(status !== 'needParameterCheck' ? { context: data.context } : {}),
				connectionID: data.connectionID
			};

			const createData = {
				...data,
				category: data.category.toUpperCase(),
				components: componentList
			};

			await axiosPrivate
				.post(url, { ...(mode === 'edit' ? editData : createData) })
				.then((response) => {
					if (response.status === 200) {
						showSuccess();
						dispatch(updateDynamicListData(DYNAMICLISTS.WHATSAPP_TEMPLATES));
						handleCloseModal();
					}
				})
				.catch((error) => {
					if (error?.response?.data?.code === 'error.WT010') {
						setError('name', { type: 'custom', message: 'error.WT010' });
					}
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
					AppLogger.error(error);
				});
		},
		[languages, status, mode, errorMessages]
	);

	const dispatchEditableState = useCallback((payload) => {
		const { status = null, date = null } = payload;
		if (!status || !date) return;
		const today = new Date().getTime();
		const updatedDate = new Date(date).getTime();

		// check by status
		switch (status) {
			case 'approved':
				if (today - updatedDate > 30 * 24 * 60 * 60 * 1000) {
					setEditableState((prev) => ({
						...prev,
						contextEditable: false,
						categoryEditable: false
					}));
					showError({
						message: 'whatsapp.editableState24'
					});
				} else {
					setEditableState((prev) => ({
						...prev,
						contextEditable: true,
						categoryEditable: false
					}));
				}
				break;
			case 'rejected':
			case 'paused':
				setEditableState((prev) => ({
					...prev,
					contextEditable: true,
					categoryEditable: true
				}));
				break;
			case 'pending':
				setEditableState((prev) => ({
					...prev
				}));
				break;
			case 'needParameterCheck':
				setEditableState({
					contextEditable: false,
					categoryEditable: false,
					parametersEditable: true
				});
		}
	}, []);

	const getTemplateByID = useCallback(async (ID) => {
		await axiosPrivate
			.post(WHATSAPP.GET_WHATSAPP_TEMPLATE, { ID })
			.then((response) => {
				const { data } = response;

				// get language from map
				// let langMap = null;
				// Object.keys(languageMapping).forEach((x) => {
				// 	if (languageMapping[x].wpCode === data?.language) {
				// 		langMap = languageMapping[x];
				// 	}
				// });

				// find main content string
				const context = data?.components?.find((x) => x.componentLocation === 'BODY')?.context ?? null;

				const headerComponent = data?.components?.find((component) => component.componentLocation === 'HEADER');
				const headerUrl = headerComponent?.parameters[0].parameterOptionalFieldValue ?? null;

				const buttonsComponent = data?.components?.find(
					(component) => component.componentLocation === 'BUTTONS'
				);
				const buttonLabel = (buttonsComponent?.context || data?.buttonLabel) ?? null;

				setStatus(data?.status ?? null);
				dispatchEditableState({
					status: data?.status ?? null,
					date: data?.lastUpdated ?? null
				});

				const componentData = [];
				let newBodyContext = undefined;
				if (data?.components?.length) {
					const componentsWithParameter = data?.components?.filter((component) => component?.parameterCount);

					if (data?.status === 'needParameterCheck') {
						componentsWithParameter?.forEach((component) => {
							for (let index = 1; index <= component.parameterCount; index += 1) {
								componentData.push({
									location: component.componentLocation,
									componentOTOId: component.componentOTOId,
									context: component.context,
									parameterName: `{{${index}}}`,
									newParameter: null
								});
							}
						});
					} else {
						const body = data?.components?.find((x) => x.componentLocation === 'BODY');
						const bodyContext = body?.context ?? null;
						if (bodyContext && body?.parameters?.length > 0) {
							let index = 1;
							newBodyContext = bodyContext.replace(/{{\d}}/g, () => {
								const parameter = body?.parameters?.find((x) => x.parameterIndex === index);
								index += 1;
								return `{{${parameter?.parameterContext}}}`;
							});
						}
					}
				}

				reset({
					ID,
					name: data?.name ?? null,
					context: newBodyContext ?? context ?? null,
					category: data?.category ? data.category.toLowerCase() : null,
					language: data?.language ?? null,
					connectionID: data?.connectionID ?? null,
					buttonLabel,
					headerUrl,
					components: componentData
				});
			})
			.catch((error) => {
				AppLogger.error(error);
			});
	}, []);

	const defaultColSpan = {
		md: 6
	};

	const whatsappConnectionColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const whatsappConnectionLabels = useMemo(() => ['Name'], []);

	useEffect(() => {
		if (mode === 'add') {
			setEditableState({
				contextEditable: true,
				categoryEditable: true
			});
			return;
		}

		if (id) {
			getTemplateByID(id);
		}
	}, [id, mode]);

	return (
		<Modal
			fullwidth
			maxWidth="large"
			name={modalName}
			className="modal-whatsapp-template"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={title}
					tooltip={['edit', 'view'].includes(mode) ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{title}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{/* templateName */}
					<FormItem label="templateName" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="name" disabled={mode !== 'add'} />
					</FormItem>
					{/* templateName */}
					{/* connectionID */}
					<FormItem label="connectionName" colSpan={defaultColSpan} isLoading={isLoading} required>
						<AutoComplete
							name="connectionID"
							value={watch('connectionID')}
							autoCompleteName="whatsappBusinessConnections"
							columns={whatsappConnectionColumns}
							selectionLabelAccessor={whatsappConnectionLabels}
							setValue={(value, firstRender) => {
								if (firstRender) return;
								setValue('connectionID', value?.id ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							valueRemovable={false}
							firstItem={{
								action: () =>
									NiceModal.show(WhatsappBusinessModal, {
										setConnection: (value) => {
											setValue(`connectionID`, value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}),
								label: <Typography color="var(--blue)">addConnection</Typography>
							}}
							disabled={mode !== 'add'}
						/>
					</FormItem>
					{/* connectionID */}
					{/* category */}
					<FormItem
						label="category"
						colSpan={defaultColSpan}
						isLoading={isLoading}
						toolTip="tooltip.whatsappTemplateCategory"
						required>
						<EnumSelect
							searchable={false}
							data={categories}
							value={watch('category')}
							name="category"
							valueRemovable={false}
							setValue={(value) => {
								setValue(`category`, value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={!editableState.categoryEditable || mode === 'view'}
						/>
					</FormItem>
					{/* category */}
					{/* language */}
					<FormItem label="language" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							name="language"
							searchable={false}
							valueRemovable={false}
							enumName={ENUMS.LANGUAGE_CODES}
							value={watch(`language`)}
							setValue={(value) => {
								setValue(`language`, value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={mode !== 'add'}
						/>
					</FormItem>
					{/* language */}
					{/* context */}
					{status !== 'needParameterCheck' && (
						<ContentCreator
							menuItems={menuItems}
							isLoading={isLoading}
							disabled={!editableState.contextEditable || mode === 'view'}
							label="bodyContent"
						/>
					)}
					{/* context */}
					{/* parameters */}
					{editableState.parametersEditable && mode === 'edit' && (
						<FormItem
							label="parameters"
							isLoading={isLoading}
							toolTip="tooltip.whatsappTemplateParameters"
							required>
							<ParameterMatcher fieldName="components" menuItems={menuItems} status={status} />
						</FormItem>
					)}
					{/* parameters */}

					<FormItem colSpan={defaultColSpan} label="imageUrl">
						<ImageInput
							placeholder="selectFile"
							name="headerUrl"
							value={watch('headerUrl')}
							setValue={(value) => {
								setValue('headerUrl', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={mode === 'view'}
						/>
					</FormItem>

					<FormItem
						label="buttonLabel"
						colSpan={defaultColSpan}
						isLoading={isLoading}
						toolTip="whatsappTemplateButtonLabel.tooltip"
						required>
						<StringInput
							name="buttonLabel"
							setValue={(value) => {
								setValue(`buttonLabel`, value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={mode === 'view'}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			{mode !== 'view' && (
				<ModalActions>
					<Button label="cancel" variant="outline" disabled={isLoading} onClick={handleCloseModal} />
					<Button
						label="save"
						disabled={!isDirty || isLoading}
						loading={isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit)}
						iconPos="right"
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default WhatsappTemplateForm;
